/**
 * Permission Service
 * 
 * Provides user permission checking based on Keycloak roles and Gobodo Role data.
 * Bridges authentication (Keycloak) with authorization (Gobodo roles).
 * 
 * Part of PE-898/PE-900: Dynamic permissions fetching
 */

import { keycloakService } from './keycloakService';
import { gobodoUserProvider } from './providers/gobodoUserProvider';
import type { Role, SearchPermissions } from './providers/roleProvider';
import { getPrimaryRole, summarizeRoles } from './rbac/rolePermissionSummary';
import { shouldBypassRoleChecks } from '../config/mvpConfig';

// Re-export types for convenience
export type { Role, SearchPermissions } from './providers/roleProvider';

// Application types (matching permissions.ts)
export type ApplicationType =
  | 'regal_main'
  | 'cybersweep'
  | 'credit_block'
  | 'credit_management';

// User permissions interface
export interface UserPermissions {
  canEdit: boolean;
  canEditCyberSweep: boolean;
  canEditCreditBlock: boolean;
  canEditCreditManagement: boolean;
  accessibleApps: ApplicationType[];
}

// Current user info
export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  keycloakRoles: string[];
  roles: Role[];
  role: Role | null;
  permissions: UserPermissions;
}

// Map Keycloak realm roles to Gobodo Role names
const KEYCLOAK_TO_GOBODO_ROLE: Record<string, string> = {
  'system_admin': 'System Administrator',
  'manager': 'Operations Manager',
  'cb_supervisor': 'Credit Block Team Lead',
  'cb_team': 'Credit Block Processor',
  'cs_supervisor': 'CyberSweep Team Lead',
  'cs_auditor': 'CyberSweep Auditor',
  'data_analyst': 'Data Analyst',
  // Legacy / backward-compat mappings
  'standard_user': 'Credit Block Processor',
  'developer': 'System Administrator',
  'read_only': 'Data Analyst',
  'CS Supervisor': 'CyberSweep Team Lead',
  'CS Team': 'CyberSweep Auditor',
  'CB Supervisor': 'Credit Block Team Lead',
  'CB Team': 'Credit Block Processor',
  'CM Supervisor': 'Operations Manager',
  'CM Team': 'Credit Block Processor',
  'System Admin': 'System Administrator',
};

/** Fallback when Gobodo returns no role — derive from Keycloak realm roles. */
function keycloakToUserPermissions(): UserPermissions {
  const userInfo = keycloakService.getUserInfo();
  if (!userInfo?.roles?.length) return DEFAULT_PERMISSIONS;
  const roles = userInfo.roles as string[];
  if (roles.includes('system_admin') || roles.includes('System Admin')) return SYSTEM_ADMIN_PERMISSIONS;

  const merged: UserPermissions = { ...DEFAULT_PERMISSIONS, accessibleApps: ['regal_main'] };
  const addApps = (...apps: ApplicationType[]) => {
    merged.accessibleApps = Array.from(new Set([...merged.accessibleApps, ...apps]));
  };

  if (roles.includes('manager')) {
    merged.canEdit = true;
    merged.canEditCyberSweep = true;
    merged.canEditCreditBlock = true;
    merged.canEditCreditManagement = true;
    addApps('cybersweep', 'credit_block', 'credit_management');
  }
  if (roles.includes('cb_supervisor') || roles.includes('cb_team')) {
    merged.canEdit = true;
    merged.canEditCreditBlock = true;
    addApps('credit_block');
  }
  if (roles.includes('cs_supervisor') || roles.includes('cs_auditor')) {
    merged.canEdit = true;
    merged.canEditCyberSweep = true;
    addApps('cybersweep');
  }
  if (roles.includes('data_analyst')) {
    addApps('cybersweep', 'credit_block');
  }
  return merged;
}

// Default permissions (read-only fallback)
const DEFAULT_PERMISSIONS: UserPermissions = {
  canEdit: false,
  canEditCyberSweep: false,
  canEditCreditBlock: false,
  canEditCreditManagement: false,
  accessibleApps: ['regal_main'],
};

// System admin permissions (full access)
const SYSTEM_ADMIN_PERMISSIONS: UserPermissions = {
  canEdit: true,
  canEditCyberSweep: true,
  canEditCreditBlock: true,
  canEditCreditManagement: true,
  accessibleApps: ['regal_main', 'cybersweep', 'credit_block', 'credit_management'],
};

// Cache for current user's role set
let cachedUserRoles: Role[] = [];
let cacheTimestamp: number = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Invalidate the cached user role
 * Call this on logout or token refresh
 */
export function invalidatePermissionCache(): void {
  cachedUserRoles = [];
  cacheTimestamp = 0;
}

/**
 * Check if cache is valid
 */
function isCacheValid(): boolean {
  return (Date.now() - cacheTimestamp) < CACHE_TTL_MS;
}

/**
 * Get the best matching Gobodo role name for Keycloak roles
 */
function mapKeycloakRoleToGobodo(keycloakRoles: string[]): string | null {
  const priorityOrder = [
    'system_admin', 'manager',
    'cb_supervisor', 'cs_supervisor', 'cb_team', 'cs_auditor',
    'data_analyst', 'developer', 'standard_user', 'read_only',
  ];
  
  for (const priority of priorityOrder) {
    if (keycloakRoles.includes(priority)) {
      return KEYCLOAK_TO_GOBODO_ROLE[priority] || null;
    }
  }
  
  // Try direct mapping for any role
  for (const role of keycloakRoles) {
    const mapped = KEYCLOAK_TO_GOBODO_ROLE[role];
    if (mapped) return mapped;
  }
  
  return null;
}

/**
 * Convert Role's searchPermissions to UserPermissions
 */
function rolesToUserPermissions(roles: Role[] | null | undefined): UserPermissions {
  const summary = summarizeRoles(roles ?? []);
  if (!summary.roles.length) return DEFAULT_PERMISSIONS;
  return {
    canEdit: summary.canEdit,
    canEditCyberSweep: summary.canEditCyberSweep,
    canEditCreditBlock: summary.canEditCreditBlock,
    canEditCreditManagement: summary.canEditCreditManagement,
    accessibleApps: summary.accessibleApps,
  };
}

/**
 * Get the current user's roles from Gobodo
 * Uses UsersApi.getSelf() to get current user, then getUserRoles() to get roles via relationships
 * Returns an empty array if user has no roles assigned (no fallback)
 * Caches result for performance
 */
export async function getCurrentUserRoles(): Promise<Role[]> {
  // Return cached if valid
  if (isCacheValid()) {
    return cachedUserRoles;
  }
  
  try {
    const userInfo = keycloakService.getUserInfo();
    if (!userInfo) {
      console.log('[PermissionService] No authenticated user');
      return [];
    }
    
    // Get current user from Gobodo UsersApi - no fallback
    const gobodoUser = await gobodoUserProvider.getSelf();
    
    if (!gobodoUser || !gobodoUser.id) {
      console.error('[PermissionService] User not found in Gobodo');
      return [];
    }
    
    // Get roles via graph traversal - no fallback
    const roles = await gobodoUserProvider.getUserRoles(gobodoUser.id);
    
    if (!roles || roles.length === 0) {
      console.warn('[PermissionService] User found in Gobodo but has no roles assigned. User ID:', gobodoUser.id);
      cachedUserRoles = [];
      cacheTimestamp = Date.now();
      return [];
    }
    
    cachedUserRoles = roles;
    cacheTimestamp = Date.now();
    console.log('[PermissionService] Resolved roles from Gobodo:', roles.map((role) => role.name), 'for user:', gobodoUser.email);
    return cachedUserRoles;
  } catch (error) {
    console.error('[PermissionService] Failed to fetch role:', error);
    return [];
  }
}

/**
 * Backward-compatible single-role accessor.
 * Returns the highest-priority role for places that still expect one display role.
 */
export async function getCurrentUserRole(): Promise<Role | null> {
  const roles = await getCurrentUserRoles();
  return getPrimaryRole(roles);
}

/**
 * Get the current user's permissions
 */
export async function getCurrentUserPermissions(): Promise<UserPermissions> {
  if (shouldBypassRoleChecks()) return { ...SYSTEM_ADMIN_PERMISSIONS };
  const roles = await getCurrentUserRoles();
  const fromGobodo = rolesToUserPermissions(roles);
  if (fromGobodo.accessibleApps.length > 1) return fromGobodo;
  return keycloakToUserPermissions();
}

/**
 * Get current user info with role and permissions
 */
export async function getCurrentUser(): Promise<CurrentUser> {
  const userInfo = keycloakService.getUserInfo();
  const roles = await getCurrentUserRoles();
  const role = getPrimaryRole(roles);
  const permissions = rolesToUserPermissions(roles);
  
  return {
    id: userInfo?.id || '',
    name: userInfo ? `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() || userInfo.email : 'Unknown User',
    email: userInfo?.email || '',
    keycloakRoles: userInfo?.roles || [],
    roles,
    role,
    permissions,
  };
}

/**
 * Check if current user can edit in a specific app context
 */
export async function canEditInContext(context: ApplicationType): Promise<boolean> {
  if (shouldBypassRoleChecks()) return true;
  const permissions = await getCurrentUserPermissions();
  
  switch (context) {
    case 'cybersweep':
      return permissions.canEditCyberSweep;
    case 'credit_block':
      return permissions.canEditCreditBlock;
    case 'credit_management':
      return permissions.canEditCreditManagement;
    case 'regal_main':
      return permissions.canEdit;
    default:
      return false;
  }
}

/**
 * Check if current user can access an app
 */
export async function canAccessApp(app: ApplicationType): Promise<boolean> {
  const permissions = await getCurrentUserPermissions();
  return permissions.accessibleApps.includes(app);
}

/**
 * Synchronous permission check using cached role
 * Use this in render functions where async is not possible
 * Returns default permissions if cache is empty
 * Falls back to Keycloak-derived permissions when Gobodo returns no role
 */
export function getCachedPermissions(): UserPermissions {
  if (shouldBypassRoleChecks()) return { ...SYSTEM_ADMIN_PERMISSIONS };
  const fromGobodo = rolesToUserPermissions(cachedUserRoles);
  if (fromGobodo.accessibleApps.length > 1) return fromGobodo;
  return keycloakToUserPermissions();
}

export function getCachedRoles(): Role[] {
  return cachedUserRoles;
}

/**
 * Synchronous current user check using cached data
 */
export function getCachedCurrentUser(): CurrentUser {
  const userInfo = keycloakService.getUserInfo();
  const permissions = getCachedPermissions();
  const role = getPrimaryRole(cachedUserRoles);
  
  return {
    id: userInfo?.id || '',
    name: userInfo ? `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() || userInfo.email : 'Unknown User',
    email: userInfo?.email || '',
    keycloakRoles: userInfo?.roles || [],
    roles: cachedUserRoles,
    role,
    permissions,
  };
}

/**
 * Check if user has a specific Keycloak role
 */
export function hasKeycloakRole(role: string): boolean {
  return keycloakService.hasRole(role);
}

/**
 * Check if user is a system admin
 */
export function isSystemAdmin(): boolean {
  return keycloakService.hasRole('system_admin') || 
         keycloakService.hasRole('System Admin') ||
         cachedUserRoles.some((role) => role.isSystem || role.name === 'System Administrator');
}
